import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, X, Trash, PaperPlaneTilt, Phone, Sparkle } from "@phosphor-icons/react";

const STAGE_COLORS = {
  "Lead": "border-t-gray-400", "À contacter": "border-t-blue-400", "Démo réservée": "border-t-amber-400",
  "Essai": "border-t-violet-400", "Client": "border-t-emerald-500", "Perdu": "border-t-red-400",
};

function icpColor(n) {
  return n === "élevé" ? "text-emerald-400" : n === "moyen" ? "text-orange-400" : "text-zinc-400";
}

export default function AeliamPipeline() {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // lead drawer

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/aeliam/leads"), api.get("/aeliam/kpis"), api.get("/aeliam/team")])
      .then(([l, k, t]) => { setLeads(l.data); setStages(k.data.stages); setTeam(t.data); })
      .catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const move = async (lead, stage) => {
    try { await api.patch(`/aeliam/leads/${lead.id}`, { stage }); setLeads((ls) => ls.map((x) => x.id === lead.id ? { ...x, stage } : x)); }
    catch { toast.error("Erreur"); }
  };
  const assign = async (lead, owner_id) => {
    const member = team.find((m) => m.id === owner_id);
    try { await api.patch(`/aeliam/leads/${lead.id}`, { owner: member?.name, owner_id }); setLeads((ls) => ls.map((x) => x.id === lead.id ? { ...x, owner: member?.name, owner_id } : x)); toast.success(`Assigné à ${member?.name}`); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (lead) => {
    if (!window.confirm(`Retirer ${lead.company_nom} du pipeline ?`)) return;
    try { await api.delete(`/aeliam/leads/${lead.id}`); setLeads((ls) => ls.filter((x) => x.id !== lead.id)); setOpen(null); toast.success("Retiré du pipeline"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="aeliam-pipeline-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><UsersThree size={22} weight="fill" className="text-white" /></div>
        <div>
          <h1 className="font-head font-bold text-2xl">Pipeline Aeliam</h1>
          <p className="text-sm text-muted-foreground">Suivi commercial collaboratif des prospects Aeliam.</p>
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Chargement…</div> : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((s) => {
            const col = leads.filter((l) => l.stage === s);
            return (
              <div key={s} className="w-72 shrink-0" data-testid={`aeliam-col-${s}`}>
                <div className={`bg-card border border-border border-t-2 ${STAGE_COLORS[s] || "border-t-gray-300"} px-3 py-2 flex items-center justify-between`}>
                  <span className="font-head font-bold text-sm">{s}</span>
                  <span className="text-xs text-muted-foreground font-mono">{col.length}</span>
                </div>
                <div className="space-y-2 mt-2 min-h-[60px]">
                  {col.map((l) => (
                    <div key={l.id} className="bg-card border border-border p-3 hover:shadow-sm cursor-pointer" data-testid={`aeliam-card-${l.id}`} onClick={() => setOpen(l)}>
                      <div className="font-medium text-sm">{l.company_nom}</div>
                      <div className="text-xs text-muted-foreground">{l.ville} {l.departement && `(${l.departement})`}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs font-bold ${icpColor(l.icp?.niveau)}`}>ICP {l.icp?.score}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[110px]">{l.owner}</span>
                      </div>
                      {l.contacts_count > 0 && <div className="text-xs text-[#0a66c2] mt-1" data-testid={`aeliam-contacts-count-${l.id}`}>{l.contacts_count} contact{l.contacts_count > 1 ? "s" : ""} touché{l.contacts_count > 1 ? "s" : ""}</div>}
                      <select data-testid={`aeliam-card-stage-${l.id}`} value={l.stage} onClick={(e) => e.stopPropagation()} onChange={(e) => move(l, e.target.value)}
                        className="mt-2 w-full border border-border text-xs px-2 py-1 bg-secondary/40">
                        {stages.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                  ))}
                  {col.length === 0 && <div className="text-xs text-muted-foreground/60 px-2 py-3 text-center border border-dashed border-border">Vide</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && <LeadDrawer lead={open} team={team} onAssign={(id) => assign(open, id)} onRemove={() => remove(open)} onChanged={load} onClose={() => setOpen(null)} />}
    </div>
  );
}

function LeadDrawer({ lead, team, onAssign, onRemove, onChanged, onClose }) {
  const [acts, setActs] = useState([]);
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null); // {connection_note, message, linkedin_search}
  const [drafting, setDrafting] = useState(false);
  const [liUrl, setLiUrl] = useState("");
  const [savingLi, setSavingLi] = useState(false);
  const [call, setCall] = useState(null);
  const [callGen, setCallGen] = useState(false);

  const loadActs = () => api.get(`/aeliam/leads/${lead.company_id}/activities`).then((r) => setActs(r.data));
  useEffect(() => {
    loadActs();
    api.get(`/aeliam/companies/${lead.company_id}`).then((r) => { setDetail(r.data); setLiUrl(r.data.dirigeant_linkedin || ""); });
    /* eslint-disable-next-line */
  }, [lead.company_id]);

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/aeliam/activities", { company_id: lead.company_id, company_nom: lead.company_nom, type: "note", title: "Note", content: note });
    setNote(""); loadActs(); toast.success("Note ajoutée");
  };
  const generate = async () => {
    setDrafting(true);
    try { const { data } = await api.post("/aeliam/draft-linkedin", { company_id: lead.company_id }); setDraft(data); }
    catch { toast.error("Erreur de rédaction"); } finally { setDrafting(false); }
  };
  const generateCall = async () => {
    setCallGen(true);
    try { const { data } = await api.post("/aeliam/draft-call", { company_id: lead.company_id }); setCall(data); }
    catch { toast.error("Erreur de génération du script"); } finally { setCallGen(false); }
  };
  const saveLiUrl = async () => {
    setSavingLi(true);
    try { await api.patch(`/aeliam/companies/${lead.company_id}`, { dirigeant_linkedin: liUrl.trim() }); toast.success("Profil LinkedIn enregistré"); setDetail((d) => ({ ...d, dirigeant_linkedin: liUrl.trim() })); }
    catch { toast.error("Erreur"); } finally { setSavingLi(false); }
  };
  const profileUrl = (liUrl && liUrl.trim()) || draft?.linkedin_search ||
    `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(((detail?.dirigeants || [])[0] || lead.company_nom))}`;

  const logContact = async (channel) => {
    try {
      const { data } = await api.post(`/aeliam/leads/${lead.id}/log-contact`, { channel });
      toast.success("Prise de contact tracée" + (data.stage !== lead.stage ? ` · étape → ${data.stage}` : ""));
      loadActs(); onChanged?.();
    } catch { toast.error("Erreur"); }
  };
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copié"); };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-end z-50" data-testid="aeliam-lead-drawer" onClick={onClose}>
      <div className="bg-card w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <span className="font-head font-bold">{lead.company_nom}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-4 text-sm">
            <div><div className="overline text-muted-foreground">Étape</div><div className="font-medium">{lead.stage}</div></div>
            <div><div className="overline text-muted-foreground">Score ICP</div><div className={`font-bold ${icpColor(lead.icp?.niveau)}`}>{lead.icp?.score} · {lead.icp?.niveau}</div></div>
            {detail?.dirigeants?.length > 0 && <div><div className="overline text-muted-foreground">Dirigeant</div><div className="font-medium">{detail.dirigeants[0]}</div></div>}
          </div>
          <div>
            <label className="overline text-muted-foreground">Assigné à</label>
            <select data-testid="aeliam-assign-owner" value={lead.owner_id || ""} onChange={(e) => onAssign(e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 bg-card">
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          {/* Profil LinkedIn du dirigeant */}
          <div>
            <label className="overline text-muted-foreground">URL LinkedIn du dirigeant (optionnel)</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="aeliam-li-url" value={liUrl} onChange={(e) => setLiUrl(e.target.value)} placeholder="https://www.linkedin.com/in/…" className="flex-1 border border-border px-3 py-2 text-xs outline-none" />
              <button data-testid="aeliam-li-url-save" onClick={saveLiUrl} disabled={savingLi} className="px-3 py-2 text-xs font-semibold border border-border hover:bg-secondary">OK</button>
            </div>
          </div>

          {/* Assistant LinkedIn conforme */}
          <div className="border border-border p-3 bg-secondary/30 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Assistant LinkedIn (IA)</span>
              <button data-testid="aeliam-draft-linkedin" onClick={generate} disabled={drafting} className="text-xs font-semibold text-indigo-400 hover:underline disabled:opacity-50">{drafting ? "Rédaction…" : "Rédiger avec l'IA"}</button>
            </div>
            {draft && <>
              <div>
                <div className="overline text-muted-foreground">Note de connexion ({(draft.connection_note || "").length}/300)</div>
                <textarea value={draft.connection_note} maxLength={300} onChange={(e) => setDraft({ ...draft, connection_note: e.target.value })} rows={2} className="w-full border border-border px-2 py-1.5 text-xs outline-none" data-testid="aeliam-connection-note" />
                <button onClick={() => copy(draft.connection_note)} className="text-xs text-indigo-400 hover:underline">Copier la note</button>
              </div>
              <div>
                <div className="overline text-muted-foreground">Message</div>
                <textarea value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} rows={5} className="w-full border border-border px-2 py-1.5 text-xs outline-none" data-testid="aeliam-linkedin-text" />
                <button onClick={() => copy(draft.message)} className="text-xs text-indigo-400 hover:underline">Copier le message</button>
              </div>
            </>}
            <a href={profileUrl} target="_blank" rel="noreferrer" data-testid="aeliam-open-linkedin"
              className="flex items-center justify-center gap-1.5 text-xs font-semibold bg-[#0a66c2] text-white py-2 hover:bg-[#08538f]">
              <PaperPlaneTilt size={13} /> {liUrl ? "Ouvrir le profil" : "Rechercher sur LinkedIn"}
            </a>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button data-testid="aeliam-log-invitation" onClick={() => logContact("invitation")} className="text-xs font-semibold border border-border py-1.5 hover:bg-secondary">J'ai invité</button>
              <button data-testid="aeliam-log-message" onClick={() => logContact("linkedin")} className="text-xs font-semibold aeliam-btn-primary py-1.5">J'ai envoyé le message</button>
            </div>
          </div>

          {/* Assistant Appel (IA) */}
          <div className="border border-border p-3 bg-secondary/30 space-y-2" data-testid="aeliam-call-assistant">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold flex items-center gap-1.5"><Phone size={14} weight="fill" /> Script d'appel (IA)</span>
              <button data-testid="aeliam-draft-call" onClick={generateCall} disabled={callGen} className="text-xs font-semibold text-indigo-400 hover:underline disabled:opacity-50">
                <span className="inline-flex items-center gap-1"><Sparkle size={12} weight="fill" /> {callGen ? "Génération…" : call ? "Régénérer" : "Générer le script"}</span>
              </button>
            </div>
            {call && (
              <div className="space-y-2 text-xs" data-testid="aeliam-call-content">
                {call.objectif && <div className="bg-card border border-border px-2 py-1.5"><b className="text-indigo-400">Objectif :</b> {call.objectif}</div>}
                {call.ouverture && <div><div className="overline text-muted-foreground">Ouverture</div><p className="whitespace-pre-line">{call.ouverture}</p></div>}
                {call.contexte && <div><div className="overline text-muted-foreground">Contexte</div><p className="whitespace-pre-line">{call.contexte}</p></div>}
                {call.questions_decouverte?.length > 0 && (
                  <div><div className="overline text-muted-foreground">Questions</div>
                    <ul className="space-y-0.5">{call.questions_decouverte.map((q, i) => <li key={`aq-${i}`} className="flex gap-1.5"><span className="text-indigo-400">•</span>{q}</li>)}</ul>
                  </div>
                )}
                {call.objections?.length > 0 && (
                  <div><div className="overline text-muted-foreground">Objections</div>
                    <div className="space-y-1.5">{call.objections.map((o, i) => (
                      <div key={`ao-${i}`} className="bg-card border border-border px-2 py-1.5"><div className="font-semibold">« {o.objection} »</div><div className="text-muted-foreground">→ {o.reponse}</div></div>
                    ))}</div>
                  </div>
                )}
                {call.conclusion && <div><div className="overline text-muted-foreground">Conclusion</div><p className="whitespace-pre-line">{call.conclusion}</p></div>}
                <button onClick={() => copy([call.objectif, call.ouverture, call.contexte, (call.questions_decouverte || []).map((q) => "- " + q).join("\n"), (call.objections || []).map((o) => "• " + o.objection + "\n  → " + o.reponse).join("\n"), call.conclusion].filter(Boolean).join("\n\n"))} className="text-xs text-indigo-400 hover:underline">Copier le script</button>
              </div>
            )}
            <button data-testid="aeliam-log-call" onClick={() => logContact("appel")} className="w-full text-xs font-semibold aeliam-btn-primary py-1.5">J'ai appelé</button>
          </div>

          <div>
            <label className="overline text-muted-foreground">Ajouter une note</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="aeliam-note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Activité, échange…" className="flex-1 border border-border px-3 py-2 text-sm outline-none" />
              <button data-testid="aeliam-note-add" onClick={addNote} className="px-3 py-2 text-sm font-semibold aeliam-btn-primary">+</button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="overline text-muted-foreground">Historique</div>
            {acts.length === 0 ? <div className="text-sm text-muted-foreground">Aucune activité.</div> : acts.map((a) => (
              <div key={a.id} className="border-l-2 border-border pl-3 py-1">
                <div className="text-sm font-medium">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.content} · {a.author} · {new Date(a.date).toLocaleDateString("fr-FR")}</div>
              </div>
            ))}
          </div>

          <button data-testid="aeliam-lead-remove" onClick={onRemove} className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"><Trash size={16} /> Retirer du pipeline</button>
        </div>
      </div>
    </div>
  );
}
